import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { Button } from "../components/ui/Button";
import { getApi } from "../lib/get-api";
import { inviteAccountMismatch } from "../lib/member-access";
import { useAuth } from "../lib/useAuth";
import { useDatasources } from "../lib/useDatasources";

type InvitePreview = {
  inviterName: string;
  projectName: string;
  datasourceName: string;
  /** MOSA-367 slice 1: so the page can name both emails on a mismatch. */
  invitedEmail?: string;
  accessRole?: "OWNER" | "EDITOR" | "VIEWER";
  accessSummary?: string;
};

type PreviewState =
  | { status: "loading" }
  | { status: "ok"; data: InvitePreview }
  | { status: "missing" };

/**
 * Accept an invite.
 *
 * The colleague arrives here from a link the founder sent them. The token
 * proves they were given it, but the API also requires the signed-in email to
 * match the invite, so a forwarded link cannot be redeemed by whoever happens
 * to hold it. That means an unauthenticated visitor has to sign in first, and
 * the token is preserved across that hop.
 *
 * Public GET /invites/:token names the inviter and workspace on a live link.
 * A 404 (deadbeef, burned) settles to a dead-link card: no email claim, no
 * sign-in hop.
 */
const AcceptInvite: React.FC = () => {
  const { token: inviteToken } = useParams<{ token: string }>();
  const { user, token, logout } = useAuth();
  const { fetchDatasources } = useDatasources();
  const navigate = useNavigate();
  const [state, setState] = useState<"working" | "done" | "error">("working");
  const [message, setMessage] = useState("");
  const [preview, setPreview] = useState<PreviewState>({ status: "loading" });

  const accept = useCallback(async () => {
    if (!inviteToken) return;
    try {
      const api = getApi({ token });
      await (api as any).request({
        path: `/invites/accept/${inviteToken}`,
        method: "POST",
        format: "json",
      });
      // The rail is driven by the datasource list, so it has to be refetched
      // before navigating or the teammate lands on an empty shell.
      await fetchDatasources();
      setState("done");
      navigate("/", { replace: true });
    } catch (error: any) {
      setState("error");
      setMessage(
        error.response?.data?.message ||
          "That invite link is not valid for this account.",
      );
    }
  }, [inviteToken, token, fetchDatasources, navigate]);

  // The invited email decides whether accepting can work at all, so the
  // preview has to land first. Posting accept and reading the 403 message
  // would name only one of the two emails the invitee needs to see.
  const mismatch =
    preview.status === "ok" && user
      ? inviteAccountMismatch(preview.data.invitedEmail, user.email)
      : null;

  useEffect(() => {
    if (!user) return;
    if (preview.status === "loading") return;
    if (mismatch) return;
    accept();
  }, [user, preview.status, mismatch, accept]);

  useEffect(() => {
    if (!inviteToken) return;
    let cancelled = false;
    (async () => {
      try {
        const api = getApi();
        const res = await (api as any).request({
          path: `/invites/${inviteToken}`,
          method: "GET",
          format: "json",
        });
        const data = (res?.data ?? res) as InvitePreview;
        if (cancelled) return;
        if (
          !data?.inviterName ||
          !(data.datasourceName || data.projectName)
        ) {
          setPreview({ status: "missing" });
          return;
        }
        setPreview({ status: "ok", data });
      } catch {
        if (!cancelled) setPreview({ status: "missing" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [inviteToken]);

  const switchAccount = () => {
    logout();
    navigate({
      pathname: "/auth",
      search: `?invite=${encodeURIComponent(inviteToken ?? "")}`,
    });
  };

  if (!user) {
    const ok = preview.status === "ok" ? preview.data : null;
    const workspace = ok
      ? ok.datasourceName || ok.projectName
      : "";
    const heading =
      preview.status === "missing"
        ? "This invite is no longer valid"
        : ok && workspace
          ? `${ok.inviterName} invited you to ${workspace}`
          : null;

    return (
      <div className="min-h-screen bg-od-bg flex flex-col items-center justify-center px-4 py-8">
        <div className="mb-8 text-center">
          <a href="https://onlydash.io" className="inline-flex min-h-11 min-w-11 items-center sm:min-h-0 sm:min-w-0 [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:min-w-11 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-od-primary-text focus-visible:ring-offset-2 focus-visible:ring-offset-od-bg">
            <h1 className="text-3xl font-bold tracking-tight text-od-text">
              OnlyDash
            </h1>
          </a>
        </div>
        <div className="max-w-lg w-full space-y-4 bg-od-surface p-8 rounded-2xl shadow-lg border border-od-border">
          <div className="text-center space-y-3">
            {heading ? (
              <h2 className="text-xl font-bold text-od-text">{heading}</h2>
            ) : null}
            {preview.status === "missing" ? (
              <p className="text-od-muted text-sm">
                Ask the person who invited you for a new link.
              </p>
            ) : null}
            {preview.status === "ok" ? (
              <>
                {ok?.accessSummary ? (
                  <p className="text-od-text text-sm font-medium">
                    {ok.accessSummary}
                  </p>
                ) : null}
                <p className="text-od-muted text-sm">
                  Sign in with the email address your invite names. OnlyDash
                  then adds you to the panel. You need no Google account.
                </p>
                <Link
                  to={{
                    pathname: "/auth",
                    search: `?invite=${encodeURIComponent(inviteToken ?? "")}`,
                  }}
                  className="inline-flex w-full justify-center bg-od-primary hover:bg-od-primary/90 text-od-primary-contrast py-2.5 px-6 rounded-lg text-base font-semibold transition-colors duration-200"
                >
                  Sign in to accept
                </Link>
              </>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  // Signed in as somebody else. Name both addresses and offer the one move
  // that fixes it, rather than a dead "not valid for this account".
  if (mismatch) {
    return (
      <div className="max-w-md space-y-3 p-6">
        <h1 className="m-0 text-lg font-semibold text-od-text">
          This invite is for another account
        </h1>
        <p className="m-0 text-sm text-od-text" data-testid="invite-mismatch">
          {mismatch}
        </p>
        <Button variant="primary" onClick={switchAccount}>
          Switch account
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-2 max-w-md">
      <h1 className="text-lg font-semibold text-od-text">
        {state === "error" ? "Invite not accepted" : "Joining the project"}
      </h1>
      <p className="text-sm text-od-muted">
        {state === "error" ? message : "One moment."}
      </p>
    </div>
  );
};

export default AcceptInvite;
